import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const authHeaders = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return { 'x-user-id': String(u?._id || u?.id || '') };
  } catch {
    return { 'x-user-id': '' };
  }
};

const api = axios.create({ baseURL: `${API_BASE_URL}/api/livestream`, timeout: 15000 });
const errMsg = (e, fallback) => e.response?.data?.error || fallback;

const CardSkeleton = () => (
  <div className="animate-pulse rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
    <div className="h-4 w-1/3 rounded bg-slate-200" />
    <div className="mt-4 h-10 rounded-lg bg-slate-100" />
    <div className="mt-3 h-10 rounded-lg bg-slate-200" />
  </div>
);

const RAW_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YT_URL_RE = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|live|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;

/**
 * Extracts the 11-character YouTube video ID from any pasted link or a raw ID.
 * Handles: youtube.com/live/ID?feature=share, youtube.com/watch?v=ID (with extra
 * params), youtu.be/ID, /embed/ID, /shorts/ID, m./music. subdomains, and raw IDs.
 * Returns null when no valid ID is found.
 */
export const extractYouTubeId = (input) => {
  if (typeof input !== 'string') return null;
  const value = input.trim();
  if (!value) return null;
  if (RAW_ID_RE.test(value)) return value;
  const match = value.match(YT_URL_RE);
  return match && RAW_ID_RE.test(match[1]) ? match[1] : null;
};

function StreamCard({ item, onChanged, notify }) {
  const [videoId, setVideoId] = useState(item.youtubeVideoId || '');
  const [busy, setBusy] = useState(false);
  const dirty = videoId.trim() !== (item.youtubeVideoId || '');

  const save = async () => {
    const extractedId = extractYouTubeId(videoId);
    if (!extractedId) return notify('error', 'Invalid YouTube Link. Please check and try again.');
    setBusy(true);
    try {
      await api.post('/manage', { classId: item.classId, youtubeVideoId: extractedId }, { headers: authHeaders() });
      setVideoId(extractedId);
      notify('success', `${item.className}: stream updated`);
      await onChanged();
    } catch (e) {
      notify('error', errMsg(e, 'Failed to update stream'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    if (!item.streamId) return notify('error', 'Save a video ID before going live');
    setBusy(true);
    try {
      await api.patch(`/toggle/${item.streamId}`, { isLive: !item.isLive }, { headers: authHeaders() });
      await onChanged();
    } catch (e) {
      notify('error', errMsg(e, 'Failed to toggle stream'));
    } finally {
      setBusy(false);
    }
  };

  const toggleRecording = async () => {
    if (!item.streamId) return notify('error', 'Save a video link first');
    setBusy(true);
    try {
      await api.patch(`/toggle/${item.streamId}`, { showRecording: !item.showRecording }, { headers: authHeaders() });
      await onChanged();
    } catch (e) {
      notify('error', errMsg(e, 'Failed to update recording visibility'));
    } finally {
      setBusy(false);
    }
  };

  const clearLink = async () => {
    if (!item.streamId || !item.youtubeVideoId) return;
    if (!window.confirm(`Clear the stream link for ${item.className}? Students will no longer see the live video or recording.`)) return;
    setBusy(true);
    try {
      await api.delete(`/clear/${item.streamId}`, { headers: authHeaders() });
      setVideoId('');
      notify('success', `${item.className}: stream link cleared`);
      await onChanged();
    } catch (e) {
      notify('error', errMsg(e, 'Failed to clear stream link'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ${item.isLive ? 'ring-red-200' : 'ring-slate-100'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="truncate text-base font-bold text-slate-800">{item.className}</h4>
          <span className={`mt-1 inline-flex items-center gap-1.5 text-xs font-semibold ${item.isLive ? 'text-red-600' : 'text-slate-400'}`}>
            <span className={`h-2 w-2 rounded-full ${item.isLive ? 'animate-pulse bg-red-600' : 'bg-slate-300'}`} />
            {item.isLive ? 'LIVE' : 'OFFLINE'}
          </span>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={item.isLive}
          aria-label={`Toggle live for ${item.className}`}
          disabled={busy || !item.streamId}
          onClick={toggle}
          className={`relative h-9 w-[4.5rem] shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${item.isLive ? 'bg-red-600' : 'bg-slate-300'}`}
        >
          <span className={`absolute top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white text-[9px] font-bold shadow transition-all ${item.isLive ? 'left-[2.25rem] text-red-600' : 'left-1 text-slate-500'}`}>
            {item.isLive ? 'ON' : 'OFF'}
          </span>
        </button>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={videoId}
          onChange={(e) => setVideoId(e.target.value)}
          placeholder="Paste YouTube link or ID (e.g. https://youtube.com/live/s_fANqlWjNk)"
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
        />
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Update Stream'}
        </button>
        <button
          type="button"
          onClick={clearLink}
          disabled={busy || !item.youtubeVideoId}
          className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          🗑️ Clear Stream Link
        </button>
      </div>

      <p className="mt-2 text-xs text-amber-700">
        ⚠️ Note: Do NOT use "Private" YouTube links as they cannot be embedded in the app. Always set your YouTube stream visibility to "Unlisted" for secure, app-only viewing.
      </p>

      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-violet-50 px-3 py-2">
        <span className="text-sm font-semibold text-violet-900">📼 Allow Students to View Recording</span>
        <button
          type="button"
          role="switch"
          aria-checked={!!item.showRecording}
          aria-label={`Allow recording view for ${item.className}`}
          disabled={busy || !item.streamId}
          onClick={toggleRecording}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${item.showRecording ? 'bg-violet-600' : 'bg-slate-300'}`}
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${item.showRecording ? 'left-6' : 'left-1'}`} />
        </button>
      </div>
    </div>
  );
}

export default function AdminStreamControl() {
  const [items, setItems] = useState([]);
  const [year, setYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');

  const notify = useCallback((type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/all', { headers: authHeaders() });
      setItems(res.data.items || []);
      setYear(res.data.academicYear || '');
      setError('');
    } catch (e) {
      setError(e.response?.status === 403 ? 'Admin access required.' : errMsg(e, 'Failed to load streams'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const liveCount = items.filter((i) => i.isLive).length;
  const visible = items.filter((i) =>
    filter === 'live' ? i.isLive : filter === 'offline' ? !i.isLive : true
  );

  return (
    <div className="w-full space-y-4 p-3 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Live Classroom Control</h2>
          <p className="text-xs text-slate-500">
            {year && `Academic year ${year} · `}{liveCount} live / {items.length} classes
          </p>
        </div>
        <div className="flex gap-1 rounded-full bg-slate-100 p-1 text-xs font-semibold">
          {['all', 'live', 'offline'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1.5 capitalize ${filter === f ? 'bg-white text-indigo-600 shadow' : 'text-slate-500'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {toast && (
        <div
          role="status"
          className={`rounded-xl px-4 py-2.5 text-sm font-medium ${toast.type === 'error' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}
        >
          {toast.text}
        </div>
      )}

      {error ? (
        <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-center">
          <p className="text-sm font-medium text-rose-700">{error}</p>
          <button type="button" onClick={() => { setLoading(true); load(); }} className="mt-3 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white">
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : visible.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-400">No classes to show.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((item) => (
            <StreamCard key={item.classId} item={item} onChanged={load} notify={notify} />
          ))}
        </div>
      )}
    </div>
  );
}
